import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ExternalLink, Image, FileImage } from 'lucide-react';
import { useSubmitVerification } from '@/hooks/use-verification';

interface VerificationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const VerificationForm: React.FC<VerificationFormProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [artworkUrl, setArtworkUrl] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [errors, setErrors] = useState<{ artwork?: string; proof?: string }>({});
  
  const submitVerification = useSubmitVerification();

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    const newErrors: { artwork?: string; proof?: string } = {};

    // Validate artwork URL
    if (!artworkUrl.trim()) {
      newErrors.artwork = 'Artwork URL is required';
    } else if (!validateUrl(artworkUrl)) {
      newErrors.artwork = 'Please enter a valid URL';
    }

    // Validate proof URL
    if (!proofUrl.trim()) {
      newErrors.proof = 'Proof URL is required';
    } else if (!validateUrl(proofUrl)) {
      newErrors.proof = 'Please enter a valid URL';
    }

    // If there are errors, show them and return
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit the verification request
    submitVerification.mutate(
      { artworkUrl, proofUrl },
      {
        onSuccess: () => {
          setArtworkUrl('');
          setProofUrl('');
          setErrors({});
          onClose();
          onSuccess?.();
        },
      }
    );
  };

  const handleClose = () => {
    setArtworkUrl('');
    setProofUrl('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit Verification Request</DialogTitle>
          <DialogDescription>
            Provide links to your artwork and proof that you created it. This helps us maintain an authentic creator community.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Artwork URL */}
          <div className="space-y-2">
            <Label htmlFor="artwork-url" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Artwork URL
            </Label>
            <Input
              id="artwork-url"
              type="url"
              placeholder="https://example.com/my-artwork.jpg"
              value={artworkUrl}
              onChange={(e) => {
                setArtworkUrl(e.target.value);
                if (errors.artwork) {
                  setErrors((prev) => ({ ...prev, artwork: undefined }));
                }
              }}
              className={errors.artwork ? 'border-destructive' : ''}
            />
            {errors.artwork && (
              <p className="text-sm text-destructive">{errors.artwork}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Link to your artwork on any platform (Twitter, Instagram, ArtStation, etc.)
            </p>
          </div>

          {/* Proof URL */}
          <div className="space-y-2">
            <Label htmlFor="proof-url" className="flex items-center gap-2">
              <FileImage className="w-4 h-4" />
              Proof URL
            </Label>
            <Input
              id="proof-url"
              type="url"
              placeholder="https://example.com/making-of.jpg"
              value={proofUrl}
              onChange={(e) => {
                setProofUrl(e.target.value);
                if (errors.proof) {
                  setErrors((prev) => ({ ...prev, proof: undefined }));
                }
              }}
              className={errors.proof ? 'border-destructive' : ''}
            />
            {errors.proof && (
              <p className="text-sm text-destructive">{errors.proof}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Link showing you created this (layers, WIP, timelapse, process video, etc.)
            </p>
          </div>

          {/* Examples Section */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Good proof examples:
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
              <li>Screenshot showing layers in your art software</li>
              <li>Process video or timelapse</li>
              <li>WIP shots from your social media</li>
              <li>Behind-the-scenes breakdown</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={submitVerification.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={submitVerification.isPending}
            >
              {submitVerification.isPending ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
